import { ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Col, List, Row, Space, Statistic, Tag, Typography } from 'antd';
import { integrationsApi } from '@/features/integrations/integrations.api';

const money = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

/**
 * Live ad-account state straight from Meta (campaign statuses + real spend), so
 * the Campaigns tab reflects what is actually running — not just our internal
 * records. Renders nothing when Meta isn't connected or returns no ads data.
 */
export function LiveMetaAdsCard({ orgId, projectId }: { orgId: string; projectId: string }) {
  const query = useQuery({
    queryKey: ['meta-insights', orgId, projectId],
    queryFn: () => integrationsApi.metaInsights(orgId, projectId),
    staleTime: 60_000,
    retry: false,
  });

  const ads = query.data?.ads;
  if (!ads) return null;

  return (
    <Card
      size="small"
      title="Live on Meta"
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Refresh
        </Button>
      }
    >
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={8}>
          <Statistic title="Active campaigns" value={ads.activeCampaigns} />
        </Col>
        <Col xs={12} sm={8}>
          <Statistic title="Spend (30d)" value={money(ads.spend30d, ads.currency)} />
        </Col>
        <Col xs={12} sm={8}>
          <Statistic title="Lifetime spend" value={money(ads.lifetimeSpend, ads.currency)} />
        </Col>
      </Row>
      {ads.campaigns.length > 0 ? (
        <List
          size="small"
          style={{ marginTop: 8 }}
          header={<Typography.Text type="secondary">Campaigns on the ad account</Typography.Text>}
          dataSource={ads.campaigns}
          renderItem={(c) => (
            <List.Item>
              <Space>
                <Tag color={c.effectiveStatus === 'ACTIVE' ? 'green' : 'default'}>
                  {c.effectiveStatus}
                </Tag>
                <Typography.Text>{c.name}</Typography.Text>
              </Space>
            </List.Item>
          )}
        />
      ) : null}
    </Card>
  );
}
